import { IColumnFilterMap } from "./column-filters";
import { IGridSortState } from "./editable-data-grid";

// KOA-5857 — last-view-per-table persistence.
//
// Persists the user's filters / sort / page size per (schema, table) in
// localStorage so closing and reopening the editor on the same table
// lands them where they left off — Supabase-style "remember my view".
// Page number is intentionally NOT persisted: dropping back into page 7
// of a different result set after a filter change feels wrong.
// Selection is also ephemeral by design.
//
// Storage key embeds a schema version so a future shape change can
// safely ignore old entries (graceful skip on shape mismatch instead
// of a crash on a stale field).

const STORAGE_PREFIX: string = "kuika-table-editor-view-v2";

// Per-column width overrides (px) keyed by column name. Absence means
// "use the table-layout default" — columns aren't required to all be
// in here; the user may have only resized a handful.
export interface IColumnWidthMap {
  [columnName: string]: number;
}

export interface IPersistedView {
  filters: IColumnFilterMap;
  sort: IGridSortState | null;
  pageSize: number;
  // Optional layout fields. Pre-layout payloads shipped without them;
  // the shape check accepts their absence and the editor falls back to
  // "no explicit widths, no hidden columns" defaults.
  columnWidths?: IColumnWidthMap;
  hiddenColumns?: string[];
}

// Inputs that scope a storage key. Datasource id isolates views across
// projects / connections (the same table name in two different DBs has
// different content). Dialect disambiguates the "no schema" case so
// SQL Server's default-dbo and Oracle's session-user schema don't
// collide. tableSchema NULL/empty/"_default" is preserved verbatim —
// distinct from the literal string "dbo" — so a user-created "dbo"
// schema on Postgres stays isolated from a missing schema.
export interface IViewScope {
  datasourceId?: string;
  dialect?: string;
  tableSchema?: string;
  tableName: string;
}

const buildKey = (scope: IViewScope): string => {
  const datasource: string = scope.datasourceId && scope.datasourceId.length > 0 ? scope.datasourceId : "_nods";
  const dialect: string = scope.dialect && scope.dialect.length > 0 ? scope.dialect : "_nodialect";
  // No-schema collapses to "_default" rather than "dbo" so the key
  // distinguishes "Oracle table in the session-user schema" from a
  // SQL Server table that's actually IN the dbo schema.
  const schema: string = scope.tableSchema && scope.tableSchema.length > 0 ? scope.tableSchema : "_default";
  return `${STORAGE_PREFIX}:${datasource}:${dialect}:${schema}:${scope.tableName}`;
};

// Type-guard shapes for the runtime validator. Promoted out of inline
// `as { ... }` casts per CLAUDE.md — even one-shot validator types get
// named interfaces.
interface IRawCandidate {
  filters?: unknown;
  sort?: unknown;
  pageSize?: unknown;
  columnWidths?: unknown;
  hiddenColumns?: unknown;
}

interface IRawSort {
  column?: unknown;
  dir?: unknown;
}

// Lightweight runtime-shape check. localStorage is a shared surface;
// anyone (other tabs, browser extensions, a previous version of this
// code) could have stashed something we don't understand. A failing
// shape check returns null and the editor falls back to defaults.
const isValidPersistedView = (parsed: unknown): parsed is IPersistedView => {
  if (parsed === null || typeof parsed !== "object") {
    return false;
  }
  const candidate = parsed as IRawCandidate;
  if (typeof candidate.pageSize !== "number" || !Number.isFinite(candidate.pageSize)) {
    return false;
  }
  if (candidate.filters === null || typeof candidate.filters !== "object") {
    return false;
  }
  if (candidate.sort !== null && (typeof candidate.sort !== "object" || candidate.sort === undefined)) {
    return false;
  }
  if (candidate.sort !== null) {
    const sort = candidate.sort as IRawSort;
    if (typeof sort.column !== "string") {
      return false;
    }
    if (sort.dir !== "asc" && sort.dir !== "desc") {
      return false;
    }
  }
  // Layout fields are optional. When present, validate shape — a
  // malformed widths map or hidden list silently drops the entry to
  // null, same as the rest of the view (fall back to defaults).
  if (candidate.columnWidths !== undefined) {
    if (candidate.columnWidths === null || typeof candidate.columnWidths !== "object") {
      return false;
    }
    for (const value of Object.values(candidate.columnWidths)) {
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return false;
      }
    }
  }
  if (candidate.hiddenColumns !== undefined) {
    if (!Array.isArray(candidate.hiddenColumns)) {
      return false;
    }
    for (const name of candidate.hiddenColumns) {
      if (typeof name !== "string") {
        return false;
      }
    }
  }
  return true;
};

// Defensive wrapper — localStorage throws on private-browsing / disabled
// storage modes (Safari notably). Treat any access failure as "no
// persisted view" rather than letting the editor crash on mount.
const safeGetItem = (key: string): string | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return null;
    }
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(key, value);
  } catch {
    // Quota exceeded, private browsing, etc. — silently drop. Users
    // get the live view; they just lose the persistence affordance.
  }
};

const safeRemoveItem = (key: string): void => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    window.localStorage.removeItem(key);
  } catch {
    // Same swallow as safeSetItem.
  }
};

export const readPersistedView = (scope: IViewScope): IPersistedView | null => {
  const raw: string | null = safeGetItem(buildKey(scope));
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isValidPersistedView(parsed)) {
    return null;
  }
  return parsed;
};

export const writePersistedView = (scope: IViewScope, view: IPersistedView): void => {
  safeSetItem(buildKey(scope), JSON.stringify(view));
};

export const clearPersistedView = (scope: IViewScope): void => {
  safeRemoveItem(buildKey(scope));
};
